import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import api, { apiErr } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Server, XCircle, CheckCircle2, Link2, Loader2, Lock } from "lucide-react";

export default function BrokerConnectors() {
  const { t } = useLang();
  const [brokers, setBrokers] = useState([]);
  const [active, setActive] = useState(null);
  const [key, setKey] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [balances, setBalances] = useState(null);

  const load = () => { api.get("/brokers").then((r) => setBrokers(r.data.brokers)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/brokers/${active.id}/connect`, { api_key: key, api_secret: secret });
      toast.success(`${active.name} ${t("connected")} ✓`);
      setActive(null); setKey(""); setSecret(""); load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const disconnect = async (id) => {
    try { await api.delete(`/brokers/${id}/connect`); setBalances(null); load(); toast.info(t("disconnect")); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const showBalances = async (id) => {
    try { const { data } = await api.get(`/brokers/${id}/balance`); setBalances({ id, data }); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="border border-[#1E2028] bg-[#0F1015] p-4">
      <div className="flex items-center gap-2 text-sm font-display font-bold mb-3">
        <Server className="w-4 h-4 text-[#FACC15]" /> {t("connectors")}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {brokers.map((b) => (
          <div key={b.id} data-testid={`broker-${b.id}`}
            className="flex items-center justify-between border border-[#1E2028] bg-[#05050A] px-3 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm text-[#D1D5DB] truncate">{b.name}</span>
              {b.connected && <span className="text-[9px] font-mono text-[#10B981] border border-[#10B981]/40 px-1">{t("connected")}</span>}
            </div>
            {!b.available ? (
              <XCircle className="w-4 h-4 text-[#6B7280] shrink-0" />
            ) : b.connected ? (
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`broker-balance-${b.id}`} onClick={() => showBalances(b.id)} className="text-[10px] font-mono text-[#FACC15] hover:underline">{t("balances")}</button>
                <button data-testid={`broker-disconnect-${b.id}`} onClick={() => disconnect(b.id)} className="text-[10px] font-mono text-[#EF4444] hover:underline">{t("disconnect")}</button>
              </div>
            ) : (
              <button data-testid={`broker-connect-${b.id}`} onClick={() => { setActive(b); setBalances(null); }}
                className="flex items-center gap-1 text-[10px] font-mono text-[#FACC15] hover:underline shrink-0">
                <Link2 className="w-3 h-3" /> {t("connect")}
              </button>
            )}
          </div>
        ))}
      </div>
      <p className="mt-3 text-[10px] text-[#6B7280] font-mono leading-relaxed">
        {t("brokerUnavailable")} · {t("brokerGeoNote")}
      </p>

      {balances && (
        <div className="mt-3 border border-[#1E2028] bg-[#05050A] p-3" data-testid="broker-balances">
          <div className="text-[10px] uppercase text-[#6B7280] font-mono mb-2">{t("balances")}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {balances.data.balances?.slice(0, 12).map((x) => (
              <div key={x.asset} className="flex justify-between text-xs font-mono border border-[#1E2028] px-2 py-1">
                <span className="text-[#9CA3AF]">{x.asset}</span><span>{x.free}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#0F1015] border-[#1E2028] text-white max-w-md" data-testid="broker-connect-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Lock className="w-4 h-4 text-[#FACC15]" /> {active?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-[11px] text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 p-2 font-mono">{t("readOnlyNote")}</div>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[#9CA3AF] font-mono">{t("apiKey")}</span>
              <input data-testid="broker-api-key" value={key} onChange={(e) => setKey(e.target.value)}
                className="mt-1 w-full bg-[#05050A] border border-[#1E2028] px-3 py-2 text-sm font-mono focus:border-[#FACC15] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[#9CA3AF] font-mono">{t("apiSecret")}</span>
              <input data-testid="broker-api-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)}
                className="mt-1 w-full bg-[#05050A] border border-[#1E2028] px-3 py-2 text-sm font-mono focus:border-[#FACC15] focus:outline-none" />
            </label>
            <button data-testid="broker-connect-submit" onClick={connect} disabled={busy || key.length < 8 || secret.length < 8}
              className="w-full bg-[#FACC15] text-[#05050A] font-semibold py-2.5 text-sm hover:bg-[#eab308] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-40">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("connect")}
            </button>
            <p className="text-[10px] text-[#6B7280] font-mono">{t("brokerGeoNote")}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
